import apiClient from '../lib/apiClient';

export interface NavigationMenuItem {
  id: number;
  translationKey: string;
  href: string;
  icon: string;
  exact: boolean;
  order: number;
  active: boolean;
  isSystem: boolean;
  createdAt: string;
  updatedAt: string;

  isAdmin?: boolean; // Optional property to indicate if the menu is for admin users
}

export interface CreateNavigationMenuDto {
  translationKey: string;
  href: string;
  icon: string;
  exact?: boolean;
  order?: number;
  active?: boolean;
  isSystem?: boolean;
}

export interface UpdateNavigationMenuDto {
  translationKey?: string;
  href?: string;
  icon?: string;
  exact?: boolean;
  order?: number;
  active?: boolean;
  isSystem?: boolean;
}

class NavigationMenuService {
  // Admin endpoints (backoffice)
  async getAll(): Promise<NavigationMenuItem[]> {
    const data = await apiClient.get('/backoffice/navigation-menu');
    return data as NavigationMenuItem[];
  }

  // Public endpoint (with optional userId for custom permissions)
  async getActive(userId?: string): Promise<NavigationMenuItem[]> {
    const url = userId 
      ? `/navigation-menu/active?userId=${encodeURIComponent(userId)}`
      : '/navigation-menu/active';
    const data = await apiClient.get(url);
    return data as NavigationMenuItem[];
  }

  // Admin endpoint (backoffice)
  async getOne(id: number): Promise<NavigationMenuItem> {
    const data = await apiClient.get(`/backoffice/navigation-menu/${id}`);
    return data as NavigationMenuItem;
  }

  // Admin endpoint (backoffice)
  async create(data: CreateNavigationMenuDto): Promise<NavigationMenuItem> {
    const response = await apiClient.post('/backoffice/navigation-menu', data);
    return response as NavigationMenuItem;
  }

  // Admin endpoint (backoffice)
  async update(id: number, data: UpdateNavigationMenuDto): Promise<NavigationMenuItem> {
    const response = await apiClient.patch(`/backoffice/navigation-menu/${id}`, data);
    return response as NavigationMenuItem;
  }

  // Admin endpoint (backoffice)
  async delete(id: number): Promise<{ message: string }> {
    const response = await apiClient.delete(`/backoffice/navigation-menu/${id}`);
    return response as { message: string };
  }

  // Public endpoint (with optional userId for custom permissions)
  async isRouteActive(href: string, userId?: string): Promise<boolean> {
    const params = new URLSearchParams({ href });
    if (userId) {
      params.append('userId', userId);
    }
    const url = `/navigation-menu/check-route?${params.toString()}`;
    const response = await apiClient.get(url);
    return response as boolean;
  }
}

export const navigationMenuService = new NavigationMenuService();
